import { ReactNode } from "react";
import { Modal } from "../Modal";
import { Button } from "../Button";
import type { ButtonProps } from "../Button";

type ConfirmationButtonProps = Omit<ButtonProps, "loading">;

export interface ConfirmationProps {
  open: boolean;
  onClose: VoidFunction;
  title: string;
  description: ReactNode;
  icon?: ReactNode;
  cancel?: ConfirmationButtonProps;
  action?: ConfirmationButtonProps;
  actionLoading?: boolean;
  testId?: string;
}

export function Confirmation({
  open,
  onClose,
  title,
  description,
  icon,
  cancel,
  action,
  actionLoading,
  testId,
}: ConfirmationProps) {
  const {
    children: cancelChildren = "Cancel",
    variant: cancelVariant = "outlined",
    onClick: cancelOnClick = onClose,
    ...cancelRest
  } = cancel ?? {};

  const {
    children: actionChildren = "Confirm",
    variant: actionVariant = "contained",
    color: actionColor = "primary",
    ...actionRest
  } = action ?? {};

  return (
    <Modal
      open={open}
      onClose={actionLoading ? () => {} : onClose}
      className="w-[400px]"
      ariaLabel={title}
      testId={testId}
    >
      <div className="flex flex-col items-center">
        {icon && <div className="mb-4">{icon}</div>}
        <div className="mb-4 flex flex-col items-center gap-[7px] text-center">
          <h3 className="m-0 text-base font-bold">{title}</h3>
          <p className="text-content-muted m-0 text-[13px] font-medium">
            {description}
          </p>
        </div>
        <div className="flex w-full gap-3">
          <Button
            variant={cancelVariant}
            onClick={cancelOnClick}
            size="lg"
            className="flex-1"
            {...cancelRest}
            disabled={actionLoading || cancelRest.disabled}
          >
            {cancelChildren}
          </Button>
          <Button
            variant={actionVariant}
            color={actionColor}
            size="lg"
            className="flex-1"
            loading={actionLoading}
            {...actionRest}
          >
            {actionChildren}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
